import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'GeckoCmsIntegration',
  data () {
    return {
      isLoading: {
        initial: false,
        geckoCms: false,
        geckoCmsKey: false,
        geckoCmsWebhook: false
      },
      geckoCmsSite: null,
      geckoCmsKeys: [],
      geckoCmsReadToken: null,
      revalidateWebhookUrl: '',
      revalidateWebhookSecret: ''
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan',
      'apiUrl'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    geckoCmsApiBaseUrl () {
      return `${String(this.apiUrl || '').replace(/\/+$/g, '')}/cms/public`
    },
    geckoCmsSiteKey () {
      return this.geckoCmsSite?.site_key || ''
    },
    geckoCmsDefaultLang () {
      return this.geckoCmsSite?.default_lang || this.activeContentplan?.language || 'en'
    },
    geckoCmsListEndpoint () {
      if (!this.geckoCmsSiteKey) return ''
      return `${this.geckoCmsApiBaseUrl}/posts?site=${this.geckoCmsSiteKey}&lang=${this.geckoCmsDefaultLang}&limit=20&offset=0`
    },
    geckoCmsDetailEndpoint () {
      if (!this.geckoCmsSiteKey) return ''
      return `${this.geckoCmsApiBaseUrl}/posts/{slug}?site=${this.geckoCmsSiteKey}&lang=${this.geckoCmsDefaultLang}`
    },
    geckoCmsEnvExample () {
      if (!this.geckoCmsSiteKey) return ''
      const token = this.geckoCmsReadToken || '<generate-a-read-token>'
      return [
        `CONTENTGECKO_CMS_API_URL=${this.geckoCmsApiBaseUrl}`,
        `CONTENTGECKO_CMS_SITE=${this.geckoCmsSiteKey}`,
        `CONTENTGECKO_CMS_TOKEN=${token}`
      ].join('\n')
    },
    geckoCmsAstroExample () {
      return [
        'const apiUrl = import.meta.env.CONTENTGECKO_CMS_API_URL',
        'const site = import.meta.env.CONTENTGECKO_CMS_SITE',
        'const token = import.meta.env.CONTENTGECKO_CMS_TOKEN',
        '',
        "const response = await fetch(apiUrl + '/posts?site=' + site + '&lang=en', {",
        '  headers: {',
        "    Authorization: 'Bearer ' + token",
        '  }',
        '})',
        '',
        "if (!response.ok) throw new Error('CMS request failed: ' + response.status)",
        'const posts = await response.json()'
      ].join('\n')
    }
  },
  methods: {
    ...mapActions([
      'contentplanGetOne',
      'getCmsSiteByContentplan',
      'postCmsSite',
      'putCmsSite',
      'postCmsSiteReadKey'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    async loadGeckoCmsSite () {
      try {
        const response = await this.getCmsSiteByContentplan({ contentplanId: this.contentplanId })
        this.setGeckoCmsSite(response?.site || null)
        this.geckoCmsKeys = response?.keys || []
      } catch (error) {
        if (error?.response?.status !== 404) {
          this.setSnackbar({
            open: true,
            text: error?.response?.data?.message || 'Failed to load Gecko CMS site',
            type: 'error'
          })
        }
        this.setGeckoCmsSite(null)
        this.geckoCmsKeys = []
      }
      this.geckoCmsReadToken = null
    },
    async createGeckoCmsSite () {
      this.isLoading.geckoCms = true
      try {
        const response = await this.postCmsSite({
          contentplanId: this.contentplanId,
          name: this.contentplanName || `Contentplan ${this.contentplanId}`,
          domain: this.activeContentplan?.domain || null,
          basePath: '/blog',
          defaultLang: this.activeContentplan?.language || 'en'
        })
        this.setGeckoCmsSite(response?.site || null)
        this.geckoCmsKeys = response?.keys || []
        this.geckoCmsReadToken = response?.readToken || null
        this.setSnackbar({
          open: true,
          text: 'Gecko CMS site created',
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || 'Failed to create Gecko CMS site',
          type: 'error'
        })
      }
      this.isLoading.geckoCms = false
    },
    setGeckoCmsSite (site) {
      this.geckoCmsSite = site
      this.revalidateWebhookUrl = site?.revalidate_webhook_url || ''
      this.revalidateWebhookSecret = ''
    },
    async saveRevalidationWebhook () {
      if (!this.geckoCmsSite?.id) return

      this.isLoading.geckoCmsWebhook = true
      try {
        const payload = {
          siteId: this.geckoCmsSite.id,
          revalidateWebhookUrl: this.revalidateWebhookUrl
        }
        if (this.revalidateWebhookSecret) payload.revalidateWebhookSecret = this.revalidateWebhookSecret

        const response = await this.putCmsSite(payload)
        this.setGeckoCmsSite(response?.site || this.geckoCmsSite)
        this.setSnackbar({
          open: true,
          text: 'Revalidation webhook saved',
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || 'Failed to save revalidation webhook',
          type: 'error'
        })
      }
      this.isLoading.geckoCmsWebhook = false
    },
    async generateGeckoCmsReadKey () {
      if (!this.geckoCmsSite?.id) return this.createGeckoCmsSite()

      this.isLoading.geckoCmsKey = true
      try {
        const response = await this.postCmsSiteReadKey({
          siteId: this.geckoCmsSite.id,
          label: `Website read key ${new Date().toISOString().slice(0, 10)}`
        })
        this.geckoCmsKeys = response?.keys || []
        this.geckoCmsReadToken = response?.readToken || null
        this.setSnackbar({
          open: true,
          text: 'Gecko CMS API key generated',
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || 'Failed to generate Gecko CMS API key',
          type: 'error'
        })
      }
      this.isLoading.geckoCmsKey = false
    },
    async copyToClipboard (text, label) {
      try {
        await navigator.clipboard.writeText(text)
        this.setSnackbar({ open: true, text: `${label} copied to clipboard`, type: 'success' })
      } catch (error) {
        this.setSnackbar({ open: true, text: 'Failed to copy to clipboard', type: 'error' })
      }
    }
  },
  async mounted () {
    this.isLoading.initial = true
    try {
      await Promise.all([
        this.contentplanGetOne({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        }),
        this.loadGeckoCmsSite()
      ])
    } finally {
      this.isLoading.initial = false
    }
  }
}
